import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Seo from "../lib/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Copy, CheckCircle2, ShieldCheck, MessageCircle, Landmark, Smartphone, UploadCloud,
  QrCode, Sparkles, Info, Wallet, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

/* Payment WhatsApp number is DIFFERENT from support number */
const PAY_WA_NUMBER = "919818158787";
const PAY_UPI_ID = "9818158787@pthdfc";
const PAY_UPI_PHONE = "9818158787";

const BANK = {
  holder: "Bhupal Singh Bisht",
  bank: "HDFC Bank",
  type: "Savings Account",
  branch: "Defence Colony, New Delhi",
  account: "01341000107234",
  ifsc: "HDFC0000134",
};

function CopyBtn({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button type="button" onClick={doCopy} data-testid={`copy-${label.toLowerCase().replace(/\s+/g,'-')}`}
      className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#D4AF37] transition-colors">
      {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : label}
    </button>
  );
}

function DetailRow({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-foreground/10 last:border-0">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/60">{label}</div>
        <div className="font-mono text-sm mt-1 break-all" data-testid={testid}>{value}</div>
      </div>
      <CopyBtn text={value} label="Copy" />
    </div>
  );
}

export default function Payment() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", transaction_id: "" });
  const [file, setFile] = useState(null); // { name, base64 }
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("File must be under 2 MB."); return; }
    const b = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    setFile({ name: f.name, base64: String(b) });
    toast.success("Screenshot attached");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments", {
        ...form,
        screenshot_name: file?.name || null,
        screenshot_data: file?.base64 || null,
      });
      setSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  const waConfirmationMsg = `Hello YourAstroHub, I have completed my payment. My name is ${form.name || "__________"}. My Transaction ID is ${form.transaction_id || "__________"}. Please confirm my consultation/course enrollment.`;
  const waLink = `https://wa.me/${PAY_WA_NUMBER}?text=${encodeURIComponent(waConfirmationMsg)}`;

  return (
    <div data-testid="payment-page" className="min-h-screen">
      <Seo
        title="Complete Your Payment Securely — UPI, Google Pay, Bank Transfer | YourAstroHub"
        description="Pay for your Vedic astrology consultation or course securely via UPI, Google Pay, Paytm, PhonePe, or Bank Transfer. Upload payment proof and confirm on WhatsApp."
        keywords="YourAstroHub Payment, UPI Payment, Google Pay, Paytm, Bank Transfer, Astrology Consultation Payment"
        path="/payment"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Payment", path: "/payment" },
        ]}
      />

      {/* HERO ---------------------------------------------------- */}
      <section className="pt-8 pb-14 md:pb-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#10B981]/10 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-8 text-center">
          <div className="eyebrow mb-4">Secure Payment</div>
          <h1 data-testid="pay-heading" className="font-serif text-4xl md:text-6xl font-light leading-[1.05]">
            Complete Your Payment <span className="gold-text italic">Securely</span>
          </h1>
          <p className="text-foreground/70 mt-6 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
            Thank you for choosing YourAstroHub. Please complete your payment using any of the options below.
            After making the payment, kindly upload the payment screenshot or share it on WhatsApp to confirm
            your booking or course enrollment.
          </p>
        </div>
      </section>

      {/* PAYMENT OPTIONS ----------------------------------------- */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-2 gap-6">
          {/* UPI / QR Card */}
          <div data-testid="upi-card" className="relative rounded-3xl p-8 md:p-10 bg-[#0B1F3A] text-[#F5E8D8] border border-[#D4AF37]/40 grain overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#D4AF37]/20 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40">
                  <Smartphone className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="eyebrow">Option 1</div>
                  <div className="font-serif text-2xl md:text-3xl">UPI / Paytm / Google Pay</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="rounded-2xl bg-white p-4 shadow-inner">
                  <img src="/assets/upi-qr.png" alt="Scan UPI QR to pay YourAstroHub" data-testid="upi-qr" className="w-full max-w-[280px] mx-auto" />
                  <div className="text-center text-[10px] uppercase tracking-[0.22em] text-[#0B1F3A]/70 mt-2 font-semibold">Scan & Pay Using Any UPI App</div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-[#D4AF37]/25 p-4 bg-[#0F284B]/50">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5E8D8]/60 mb-2">Paytm / Google Pay Number</div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-mono text-lg md:text-xl text-[#D4AF37]" data-testid="upi-phone">{PAY_UPI_PHONE}</div>
                      <CopyBtn text={PAY_UPI_PHONE} />
                    </div>
                  </div>
                  <div className="rounded-2xl border border-[#D4AF37]/25 p-4 bg-[#0F284B]/50">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5E8D8]/60 mb-2">UPI ID</div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-mono text-base md:text-lg text-[#D4AF37] break-all" data-testid="upi-id">{PAY_UPI_ID}</div>
                      <CopyBtn text={PAY_UPI_ID} />
                    </div>
                  </div>

                  <div className="pt-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#F5E8D8]/60 mb-2">Compatible Apps</div>
                    <div className="flex flex-wrap gap-2">
                      {["Google Pay", "Paytm", "PhonePe", "BHIM", "Any UPI App"].map((a) => (
                        <span key={a} className="text-xs px-3 py-1 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981]">{a}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Transfer Card */}
          <div data-testid="bank-card" className="relative rounded-3xl p-8 md:p-10 bg-background border border-[#10B981]/40 grain overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#10B981]/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[#10B981]/10 border border-[#10B981]/40">
                  <Landmark className="w-5 h-5 text-[#0E9F6E]" />
                </div>
                <div>
                  <div className="eyebrow" style={{ color: "#0E9F6E" }}>Option 2</div>
                  <div className="font-serif text-2xl md:text-3xl">Bank Transfer</div>
                </div>
              </div>

              <div className="rounded-2xl border border-foreground/10 p-5 bg-[#F4EFE6]/70 dark:bg-[#0F284B]/70">
                <DetailRow label="Account Holder" value={BANK.holder} testid="bank-holder" />
                <DetailRow label="Bank" value={BANK.bank} testid="bank-name" />
                <DetailRow label="Account Type" value={BANK.type} testid="bank-type" />
                <DetailRow label="Branch" value={BANK.branch} testid="bank-branch" />
                <DetailRow label="Account Number" value={BANK.account} testid="bank-account" />
                <DetailRow label="IFSC Code" value={BANK.ifsc} testid="bank-ifsc" />
              </div>

              <div className="mt-5 flex items-start gap-2 text-xs text-foreground/70">
                <Info className="w-4 h-4 mt-0.5 text-[#0E9F6E] shrink-0" />
                <span>Add your name and Transaction ID in the transfer remarks so we can reconcile faster.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UPLOAD PAYMENT PROOF ------------------------------------ */}
      <section ref={formRef} data-testid="proof-section" className="py-16 md:py-20 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="eyebrow mb-3">Confirm Your Payment</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Upload Payment <span className="gold-text italic">Proof</span></h2>
            <p className="text-foreground/70 mt-4">Share your transaction details and we&apos;ll confirm your booking within a few hours.</p>
          </div>

          {submitted ? (
            <div data-testid="proof-success" className="rounded-3xl border border-[#0E9F6E]/40 bg-background p-10 text-center">
              <div className="w-14 h-14 rounded-full grid place-items-center bg-[#10B981]/15 border border-[#10B981]/40 mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7 text-[#0E9F6E]" />
              </div>
              <h3 className="font-serif text-3xl mb-3">Thank you for your payment</h3>
              <p className="text-foreground/70 mb-2">
                We&apos;ve received your details. Please click the button below to send your payment confirmation on WhatsApp.
              </p>
              <p className="text-xs text-foreground/60 mb-8">Reference: <code className="text-[#D4AF37]">{submitted.id.slice(0,8)}</code></p>
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="confirm-wa">
                <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                  <MessageCircle className="w-4 h-4 mr-2" />Confirm Payment on WhatsApp
                </Button>
              </a>
              <div className="text-xs text-foreground/60 mt-4">WhatsApp: +91 98181 58787</div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-foreground/10 bg-background p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label htmlFor="p-name">Full Name</Label><Input id="p-name" data-testid="proof-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-full mt-2" /></div>
                <div><Label htmlFor="p-phone">Mobile Number</Label><Input id="p-phone" data-testid="proof-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 ..." className="rounded-full mt-2" /></div>
                <div><Label htmlFor="p-email">Email (Optional)</Label><Input id="p-email" data-testid="proof-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-full mt-2" /></div>
                <div><Label htmlFor="p-service">Consultation / Course Selected</Label><Input id="p-service" data-testid="proof-service" required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="e.g. Detailed Horoscope Consultation" className="rounded-full mt-2" /></div>
                <div className="md:col-span-2"><Label htmlFor="p-txn">Transaction ID</Label><Input id="p-txn" data-testid="proof-txn" required value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} placeholder="Enter the UPI / bank reference number" className="rounded-full mt-2 font-mono" /></div>
              </div>

              <div>
                <Label>Upload Payment Screenshot</Label>
                <label className="mt-2 flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-foreground/20 hover:border-[#D4AF37] cursor-pointer transition-colors">
                  <div className="w-10 h-10 rounded-full grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <UploadCloud className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div className="text-sm flex-1">
                    <div className="font-medium">{file ? file.name : "Click to upload payment screenshot"}</div>
                    <div className="text-xs text-foreground/60">JPG / PNG / PDF · Max 2 MB</div>
                  </div>
                  <input type="file" accept="image/*,application/pdf" data-testid="proof-file" className="hidden" onChange={onFilePick} />
                </label>
              </div>

              <Button type="submit" data-testid="proof-submit" disabled={busy} size="lg" className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold py-6 disabled:opacity-50">
                {busy ? "Submitting..." : "Submit Payment Proof"}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* SECURITY NOTICE ---------------------------------------- */}
      <section data-testid="security-notice" className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="rounded-3xl border-2 border-[#0E9F6E]/40 bg-[#10B981]/5 p-8 md:p-10 grid md:grid-cols-[auto_1fr] gap-6 items-start">
            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[#10B981]/15 border border-[#10B981]/40 shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#0E9F6E]" />
            </div>
            <div>
              <div className="eyebrow mb-2" style={{ color: "#0E9F6E" }}>Security & Verification</div>
              <h3 className="font-serif text-2xl md:text-3xl mb-4">Your payment is safe with us.</h3>
              <ul className="space-y-2.5 text-sm md:text-base text-foreground/80">
                {[
                  "Payments are processed directly to the official YourAstroHub account.",
                  `Please verify the UPI ID (${PAY_UPI_ID}) before making payment.`,
                  "Keep your payment receipt until your booking is confirmed.",
                  "You will receive confirmation via WhatsApp or Email after verification.",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 mt-1 text-[#0E9F6E] shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONFIRM ON WHATSAPP DIRECT (also standalone) */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="rounded-3xl border border-[#D4AF37]/40 bg-[#0B1F3A] text-[#F5E8D8] p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#25D366]/20 blur-3xl" />
            <div className="relative">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight">Already paid? Confirm on <span className="gold-text italic">WhatsApp.</span></h3>
              <p className="text-[#F5E8D8]/70 mt-4 max-w-xl mx-auto">Send us your transaction reference on WhatsApp and we&apos;ll activate your booking or course immediately.</p>
              <a href={waLink} target="_blank" rel="noreferrer" className="inline-block mt-6" data-testid="direct-wa-confirm">
                <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                  <MessageCircle className="w-4 h-4 mr-2" />Confirm Payment on WhatsApp
                </Button>
              </a>
              <div className="text-xs text-[#F5E8D8]/60 mt-3">WhatsApp: +91 98181 58787</div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky "Pay Now" (mobile-first) */}
      {showSticky && (
        <button onClick={scrollToForm} data-testid="sticky-pay"
          className="fixed bottom-24 md:bottom-6 left-6 z-40">
          <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-[#D4AF37] text-[#0B1F3A] font-semibold shadow-2xl border border-[#0B1F3A]/10 hover:bg-[#AD974F] transition-all">
            <Wallet className="w-4 h-4" />Pay Now
          </div>
        </button>
      )}
    </div>
  );
}
